import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useGetDeviceDeploymentsQuery } from '@store/api/deploymentsApi';
import { StatusChip } from '../common/StatusChip';
import { dateTime } from '../common/tableCells';
import { KIND_LABEL, targetStatus } from '../deployments/labels';

interface DeviceUpdateHistoryProps {
  deviceId: string;
}

const DeviceUpdateHistory: React.FC<DeviceUpdateHistoryProps> = ({ deviceId }) => {
  const {
    data: deployments = [],
    isLoading: loading,
    isError,
  } = useGetDeviceDeploymentsQuery({ deviceId, limit: 20 });
  const error = isError ? 'Failed to load deployment history' : null;

  const formatDuration = (startStr: string, endStr?: string | null): string => {
    if (!endStr) return 'In progress...';
    const start = new Date(startStr).getTime();
    const end = new Date(endStr).getTime();
    const seconds = Math.floor((end - start) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (deployments.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1" sx={{
            color: 'text.secondary'
          }}>
            No update history available for this device.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Update History
        </Typography>
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                bgcolor: 'action.hover',
                '& .MuiTableCell-head': {
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  letterSpacing: 0,
                  py: 2,
                },
              }}
            >
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Update</TableCell>
                <TableCell>Target Version</TableCell>
                <TableCell>Previous Version</TableCell>
                <TableCell>Initiated</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Initiated By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deployments.map((deployment) => (
                <TableRow key={deployment.deployment_id}>
                  <TableCell>
                    <StatusChip status={targetStatus(deployment)} type="deployment" />
                  </TableCell>
                  <TableCell>{KIND_LABEL[deployment.kind]}</TableCell>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/deployments/${deployment.deployment_id}`}
                      sx={{
                        fontWeight: 'bold'
                      }}
                    >
                      {deployment.version}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{
                      color: 'text.secondary'
                    }}>
                      {deployment.previous_version || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{dateTime(deployment.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDuration(deployment.created_at, deployment.completed_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{deployment.created_by}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {deployments.some((d) => d.error) && (
          <Box sx={{
            mt: 2
          }}>
            <Typography variant="subtitle2" gutterBottom>
              Error Details:
            </Typography>
            {deployments
              .filter((d) => d.error)
              .map((deployment) => (
                <Alert key={deployment.deployment_id} severity="error" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{deployment.version}:</strong> {deployment.error}
                  </Typography>
                </Alert>
              ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceUpdateHistory;
